import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { InputField } from "../Form/InputField";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { applyFilter, getFilter } from "../../service/Filter/filterService";
import { notifyError } from "../ToastContainer/Toast/Toast";
import { buildFilterPayload } from "./Utils/GenericFilter.helper";
import { useDebouncedSearch } from "./Hooks/useDebouncedSearch";
import FilterCheckboxItem from "./Components/FilterCheckboxItem";
import { style } from "./Style/GenericFilter.style";
import { useLocation } from "react-router-dom";



interface IGenericFilter {
  data?: any;
  resetCallBack?: () => void;
  selectListState?: React.Dispatch<React.SetStateAction<any[]>>;
  apiFunction?: (pro: string) => Promise<void>;
  entity?: string;
  handleGetFetchData?: (data?: any, isFromFilter?: boolean) => void;
  store_id?: string;
  page?: number;
  limit?: number;
}

interface TypeInitialValues {
  search: string;
  genericFilterData: any[];
  selectList: any;
}

const GenericFilter: React.FC<IGenericFilter> = ({
  data,
  resetCallBack,
  selectListState,
  apiFunction,
  entity,
  handleGetFetchData,
  store_id,
  page,
  limit,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const [openList, setOpenList] = React.useState<{ [key: string]: boolean }>({});

  const [isApplied, setIsApplied] = React.useState<boolean>(false);

  const [activeFilterPayload, setActiveFilterPayload] = React.useState<any>(null);

  const open = Boolean(anchorEl);

  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  const location = useLocation();
  const pathname = location.pathname;
  const genericPlaceholder = pathname.replace(/\/$/, "").split("/").pop();

  const [initialValues, setInitialValues] = React.useState<TypeInitialValues>({
    search: "",
    genericFilterData: [],
    selectList: {},
  });

  // Ref to store current selectList from Formik (needed for hook callback)
  const selectListRef = React.useRef<any>({});
  
  // Refs for stable checkbox handler
  const setFieldValueRef = React.useRef<((field: string, value: any) => void) | null>(null);
  const valuesRef = React.useRef<any>({});

  // Move hook to top level - use ref to access current selectList
  const { searchValue, setSearchValue } = useDebouncedSearch(
    (debouncedSearchValue) => {
      const trimmed = (debouncedSearchValue || "").trim();
      // When search is cleared: call API without search filter (same as onReset / clear + Enter)
      if (trimmed.length === 0) {
        const resetSearchPayload: any = {
          ...(selectListRef.current && Object.keys(selectListRef.current).length > 0 && { ...selectListRef.current }),
          entity: entity,
          ...(store_id && { store_id: Number(store_id) }),
          page: page ?? 1,
          limit: limit ?? 10,
        };
        setActiveFilterPayload(
          selectListRef.current && Object.keys(selectListRef.current).length > 0
            ? { ...selectListRef.current, entity: entity, ...(store_id && { store_id: Number(store_id) }) }
            : { entity: entity, ...(store_id && { store_id: Number(store_id) }) }
        );
        applyFiltersWithErrorHandling(resetSearchPayload);
        return;
      }
      // Trigger if search has 3+ characters
      if (trimmed.length > 2) {
        const searchPayload = {
          ...(selectListRef.current && Object.keys(selectListRef.current).length > 0 && { ...selectListRef.current }),
          search: trimmed,
          entity: entity,
          ...(store_id && { store_id: Number(store_id) }),
          page: page ?? 1,
          limit: limit ?? 10,
        };

        setActiveFilterPayload((prev) => ({
          ...(prev || {}),
          search: trimmed,
          entity: entity,
          ...(store_id && { store_id: Number(store_id) }),
          ...(selectListRef.current && Object.keys(selectListRef.current).length > 0 && {
            ...selectListRef.current,
          }),
        }));

        applyFiltersWithErrorHandling(searchPayload);
      }
    },
    500, // 500ms delay
  );

  // Utility Functions - Extracted to reduce duplication
  const isFilterEmpty = (selectList: any) => {
    return Object.values(selectList || {}).every(
      (val) =>
        (Array.isArray(val) && val.length === 0) ||
        (typeof val === "object" &&
          val !== null &&
          Object.keys(val).length === 0)
    );
  };

  // Memoized filter option transformation function
  const transformFilterOptions = React.useCallback(
    (data: any) => {
      if (data.key === "category_id") {
        return data.options.filter((opt: any) =>
          process.env.REACT_APP_ORG_CODE === "tazty"
            ? opt.name.trim() === "F&B"
            : opt.name
        );
      }
      return data.options;
    },
    []
  );

  // Stable checkbox toggle handler (memoized at component level, uses refs for current values)
  const handleCheckboxToggle = React.useCallback(
    (filterKey: string, optionId: number | string, isChecked: boolean) => {
      if (!setFieldValueRef.current) return;
      
      const currentValues = valuesRef.current;
      if (isChecked) {
        setFieldValueRef.current(
          `selectList.${filterKey}`,
          [
            ...(currentValues.selectList?.[filterKey] || []),
            optionId,
          ]
        );
      } else {
        setFieldValueRef.current(
          `selectList.${filterKey}`,
          currentValues.selectList?.[filterKey]?.filter(
            (id: any) => id !== optionId
          ) || []
        );
      }
    },
    [] // Empty deps - function is stable, uses refs for current values
  );

  
  const handleFilterError = (error: any) => {
    notifyError("Unable to apply filters. Please try again.");
  };

  const applyFiltersWithErrorHandling = async (payload: any) => {
    try {
      const response = await applyFilter(payload);
      if (response.status === 200 || response.status === 201) {
        handleGetFetchData(response?.data?.data, true);
      }
    } catch (error) {
      handleFilterError(error);
    }
  };

  const handleFetchGetApi = async () => {
    if (!entity) {
      return;
    }

    try {
      const response = await getFilter(entity, store_id);
      const filterData = response.data?.data || {};
      if (
        Object.keys(filterData)?.length > 0 &&
        initialValues.genericFilterData?.length === 0
      ) {
        setInitialValues((prev) => ({
          ...prev,
          genericFilterData: filterData.filters,
        }));
      }
    } catch {
      // Filter metadata is optional — do not block the list page on network failure
    }
  };

  React.useEffect(() => {
    if (!openList) {
      initialOpenUpdate(true);
    }

    if (entity) {
      void handleFetchGetApi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle page changes - reapply filters if active
  React.useEffect(() => {
    if (activeFilterPayload && page) {
      // Build complete payload with current page and limit
      const completePayload = {
        ...activeFilterPayload,
        page: page ?? 1,
        limit: limit ?? 10,
      };
      applyFiltersWithErrorHandling(completePayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const initialOpenUpdate = (value: boolean) => {
    if (data && data?.length > 0) {
      const initialOpenList: { [key: string]: boolean } = {};
      data.forEach(
        (data: { key: string | number }) => (initialOpenList[data.key] = value)
      );
      setOpenList(initialOpenList);
    }
  };

  const handleSubmit = async (values: any, searchPayload?: any) => {
    const postPayload = buildFilterPayload(values, true, entity, store_id, page, limit);
    const isSelectListEmpty = isFilterEmpty(values.selectList);
    if (isSelectListEmpty && !searchPayload && !values.search && isApplied) {
      notifyError("Please select at least one filter before applying.");
      setIsApplied(false);
      return;
    } else {
      // Update active filter payload with both selectList and search
      setActiveFilterPayload({
        ...(values.selectList || {}),
        ...(values.search && values.search.trim().length > 0 && { search: values.search }),
        entity: entity,
        ...(store_id && { store_id: Number(store_id) }),
      });
      await applyFiltersWithErrorHandling(
        searchPayload ? searchPayload : postPayload
      );
    }

    if (selectListState) {
      selectListState((prev) => [...prev, values.selectList || {}]);
    }
  };

  const handleButtonClick = (
    value: any,
    e?: React.MouseEvent<HTMLButtonElement>,
    resetForm?: () => void,
    setFieldValue?: (field: string, value: any) => void,
    currentSearchValue?: string
  ) => {
    if (value === "close") {
      initialOpenUpdate(false);
      setAnchorEl(null);
    } else if (value === "open" && e) {
      setAnchorEl(e.currentTarget);
    } else if (value !== "close" && value !== "open" && value !== "reset") {
      setOpenList((prev) => {
        const newOpenList = {};
        newOpenList[value] = !prev[value];
        return newOpenList;
      });
    } else if (value === "reset" && resetForm && setFieldValue) {
      // Clear only selectList, preserve search value
      setFieldValue("selectList", {});
      
      if (selectListState && resetCallBack) {
        selectListState([]);
        resetCallBack();
      }

      // Build payload without selectList keys, but with search if it exists
      const clearFiltersPayload: any = {
        entity: entity,
        ...(store_id && { store_id: Number(store_id) }),
        page: page ?? 1,
        limit: limit ?? 10,
      };

      // Include search if it exists
      if (currentSearchValue && currentSearchValue.trim().length > 0) {
        clearFiltersPayload.search = currentSearchValue;
      }

      // Update active filter payload - remove selectList but keep search
      setActiveFilterPayload(
        currentSearchValue && currentSearchValue.trim().length > 0
          ? {
              search: currentSearchValue,
              entity: entity,
              ...(store_id && { store_id: Number(store_id) }),
            }
          : {
              entity: entity,
              ...(store_id && { store_id: Number(store_id) }),
            }
      );

      // Call applyFilter API with payload without selectList
      applyFiltersWithErrorHandling(clearFiltersPayload);
    }
  };

  

  return (
    <>
      <Card sx={{ width: "100%" }}>
        <CardContent>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={(values: any) => {
              handleSubmit(values);
            }}
          >
            {({
              values,
              setFieldValue,
              handleChange,
              submitForm,
              resetForm,
            }) => {
              // Update refs with current values for stable handler
              selectListRef.current = values.selectList || {};
              setFieldValueRef.current = setFieldValue;
              valuesRef.current = values;

              const isFiltersEmpty = isFilterEmpty(values.selectList);

              return (
                <Form>
                  <Box sx={{ flexGrow: 1, alignItems: "center", display: "flex", justifyContent: "space-between" }}>
                    <Grid
                      container
                      spacing={4}
                      rowSpacing={1}
                      columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                    >
                      <Grid item xs={3}>
                        {/* Search Input Field */}
                        <InputField
                          inputRef={searchInputRef}
                          label=""
                          name="search"
                          value={searchValue || values.search}
                          placeholder={`Search ${genericPlaceholder}...`}
                          isFilter={true}
                          onReset={async () => {
                            // Clear search field
                            setSearchValue("");
                            setFieldValue("search", "");
                            // Update initialValues to preserve selectList but clear search
                            setInitialValues((prev) => ({
                              ...prev,
                              search: "",
                              // selectList is preserved via ...prev
                            }));
                            
                            // Build payload without search key, but with selectList spread at root level if it exists
                            const resetSearchPayload: any = {
                              ...(values.selectList && Object.keys(values.selectList).length > 0 && { ...values.selectList }),
                              entity: entity,
                              ...(store_id && { store_id: Number(store_id) }),
                              page: page ?? 1,
                              limit: limit ?? 10,
                            };

                            // Update active filter payload - remove search but keep selectList
                            setActiveFilterPayload(
                              values.selectList && Object.keys(values.selectList).length > 0
                                ? {
                                    ...values.selectList,
                                    entity: entity,
                                    ...(store_id && { store_id: Number(store_id) }),
                                  }
                                : {
                                    entity: entity,
                                    ...(store_id && { store_id: Number(store_id) }),
                                  }
                            );

                            // Call applyFilter API with payload without search
                            await applyFiltersWithErrorHandling(resetSearchPayload);
                          }}
                          onFocus={() => {
                            if(Object.keys(values.selectList).length === 0) {
                              setIsApplied(false);
                            }
                          }}

                          onChange={(e) => {
                            const rawValue = e.target.value;
                            const processedValue = rawValue.toLowerCase(); 
                            
                            // Update Formik value directly (don't update initialValues to avoid reinitialization)
                            setFieldValue("search", processedValue);
                            
                            // Update debounced search hook
                            setSearchValue(processedValue);
                            
                            if (Object.keys(values.selectList).length === 0) {
                              setIsApplied(false);
                            }
                          }}
                          // onKeyDown={(e) => {
                          //   if (e.key === "Backspace" && values.search === "") {  
                          //     applyFiltersWithErrorHandling({
                          //       ...activeFilterPayload,
                          //       search: "",
                          //     });
                          //   }
                          // }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          sx={{
                            ml: 1,
                            mt: 0.5,
                          }}
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={(e) => handleButtonClick("open", e)}
                        >
                          <FilterListIcon />
                        </Button>
                        {!isFiltersEmpty && (
                          <Tooltip title="Clear Selected Filters" placement="right" arrow>
                            <Button
                              sx={{
                                ml: 1,
                                mt: 0.5,
                              }}
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() =>
                                handleButtonClick("reset", undefined, resetForm, setFieldValue, values.search || searchValue)
                              }
                            >
                              <RestartAltIcon />
                            </Button>
                          </Tooltip>
                        )}
                      </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 0.5 }}>
                      <Button 
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setFieldValue("search", "");
                          setFieldValue("selectList", {});
                          setInitialValues((prev) => ({
                            ...prev,
                            search: "",
                            selectList: {},
                          }));
                          setActiveFilterPayload(null);
                          setIsApplied(false);
                          setSearchValue("");
                          handleGetFetchData({}, false);
                        }}
                      >
                        Refresh
                      </Button>
                    </Box>
                  </Box>
                  <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => handleButtonClick("close")}
                    anchorOrigin={{ vertical: "center", horizontal: "right" }}
                    transformOrigin={{ vertical: "center", horizontal: "left" }}
                    sx={{ position: "fixed" }}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        overflowY: "auto",
                        bgcolor: "background.paper",
                      }}
                    >
                      <List
                        sx={style}
                        subheader={
                          <ListSubheader
                            sx={{ maxWidth: 400, bgcolor: "background.paper" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mt: 2,
                              }}
                            >
                              <Button
                                onClick={() => handleButtonClick("close")}
                              >
                                <CloseIcon
                                  sx={{
                                    height: 1,
                                    width: 20,
                                    cursor: "pointer",
                                    // color:'white'
                                  }}
                                  display="inline"
                                />
                                <Typography
                                  variant="h6"
                                  sx={{ pl: 1.5 }}
                                  display={"inline"}
                                  fontWeight={"bold"}
                                >
                                  Filter
                                </Typography>
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                // disabled={
                                //   !values.search &&
                                //   isFilterEmpty(values.selectList)
                                // }
                                onClick={(e) => {
                                  setIsApplied(true);
                                  e.stopPropagation();
                                  submitForm();
                                  handleButtonClick("close");
                                }}
                              >
                                Apply
                              </Button>
                            </Box>
                          </ListSubheader>
                        }
                      >
                        {!values.genericFilterData ||
                        values.genericFilterData.length === 0 ? (
                          <ListItemButton sx={{ width: "100%" }}>
                            <ListItemText
                              primary={
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography variant="subtitle2">
                                    No Data Found
                                  </Typography>
                                </Box>
                              }
                            ></ListItemText>
                          </ListItemButton>
                        ) : (
                          values?.genericFilterData?.map(
                            (data: any, index: number) => {
                              const isOpen = openList[data.name] || false;
                              // Transformed options (computed once per data change)
                              const transformedOptions = transformFilterOptions(data);
                              return (
                                <React.Fragment key={index}>
                                  <ListItemButton
                                    sx={{ width: "100%" }}
                                    onClick={() =>
                                      handleButtonClick(data?.name)
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        <Box
                                          sx={{
                                            display: "flex",
                                            // flexDirection: "row",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 16,
                                          }}
                                        >
                                          <Typography variant="subtitle2">
                                            {data.name}
                                          </Typography>
                                          <Box sx={{ ml: 1 }}>
                                            {isOpen ? (
                                              <ExpandLess />
                                            ) : (
                                              <ExpandMore />
                                            )}
                                          </Box>
                                        </Box>
                                      }
                                    ></ListItemText>
                                  </ListItemButton>
                                  <Collapse
                                    in={isOpen}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <List component={"div"} disablePadding>
                                      <ListItemButton
                                        sx={{ pl: 1, width: "100%" }}
                                      >
                                        <ListItemText
                                          primary={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                              }}
                                            >
                                              {transformedOptions?.map(
                                                (value: any, i: number) => {
                                                  const checked =
                                                    values.selectList?.[
                                                      data.key
                                                    ]?.includes(value.id) ??
                                                    false;
                                                  return (
                                                    <FilterCheckboxItem
                                                      key={`${data.key}-${value.id}-${i}`}
                                                      option={value}
                                                      filterKey={data.key}
                                                      checked={checked}
                                                      onToggle={handleCheckboxToggle}
                                                    />
                                                  );
                                                }
                                              )}
                                            </Box>
                                          }
                                        />
                                      </ListItemButton>
                                    </List>
                                  </Collapse>

                                  <Divider component={"li"} />
                                </React.Fragment>
                              );
                            }
                          )
                        )}
                      </List>
                    </Card>
                  </Popover>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default React.memo(GenericFilter);
